import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { Button } from '../button';
import { Dialog, DialogBody } from '../dialog';
import { AsignadosBubbles } from '../AsignadoBubble';
import { PendientesPanel } from '../PendientesPanel';
import { useUser } from '../UserContext';
import { useOtAcciones, useOtMensajes } from '../../hooks/useOrdenesTrabajo';
import OtHilo from './OtHilo';
import OtDialog from './OtDialog';
import OtCerrarDialog from './OtCerrarDialog';
import OtPasarFaseDialog from './OtPasarFaseDialog';
import OtRechazarDialog from './OtRechazarDialog';
import OtAprobarDialog from './OtAprobarDialog';
import { tipoBadgeClass } from './OtTarjeta';
import {
  OT_RESULTADO_LABELS, MOTIVO_MAX_LEN,
  folioTexto, isAbierta, normalizarAsignados, diasAbierta, diasEnFase, faseDe, fasesDe, estadoFase, diasObjetivoTotal,
  esCoordinador, faseEstaTerminada, esAprobador,
} from '../../utils/ordenesTrabajo';
import {
  XMarkIcon, PencilSquareIcon, ArrowTopRightOnSquareIcon, ChevronRightIcon, CheckIcon, ShieldCheckIcon,
} from '@heroicons/react/24/outline';

const formatFecha = (ms) => (ms ? new Date(Number(ms)).toLocaleDateString('es-MX', {
  day: 'numeric', month: 'short', year: 'numeric',
}) : '');
const formatFechaHora = (ms) => (ms ? new Date(Number(ms)).toLocaleString('es-MX', {
  day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
}) : '');

const field =
  'w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm outline-none ' +
  'focus:border-azul-surelia focus:ring-1 focus:ring-azul-surelia';

/**
 * The OT, open: everything the team does on it in one modal.
 *
 * Header (folio, tipo, nombre, cierre), the relation, the fase block (numbered
 * progress; "Fase terminada" for whoever works it; "Avanzar a <siguiente>" and
 * "Mover a otra fase" for coordinadores, both through OtPasarFaseDialog),
 * equipos and asignados editable in place, then two columns: Pendientes on
 * the left, the Hilo on the right.
 *
 * @param {Object} props
 * @param {Object|null} props.ot  - decorated row from useOrdenesTrabajo (live)
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose
 */
export function OtView({ ot, isOpen, onClose }) {
  const navigate = useNavigate();
  const { user, isOwner } = useUser();
  const {
    terminarFase, asignar, setEquipos, cerrar, reabrir, enviarMensaje,
    miembros, config,
  } = useOtAcciones();
  const { mensajes, loading: mensajesLoading } = useOtMensajes(isOpen ? ot?.id : null);

  const [editOpen, setEditOpen] = useState(false);
  const [cierreModo, setCierreModo] = useState(null); // 'cerrar' | 'reabrir'
  const [terminando, setTerminando] = useState(false);
  const [notaTermino, setNotaTermino] = useState('');
  const [pasar, setPasar] = useState(null); // { faseId } while the relevo dialog is open
  const [rechazarOpen, setRechazarOpen] = useState(false);
  const [aprobarOpen, setAprobarOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const abierta = isAbierta(ot);
  const fases = useMemo(() => fasesDe(config, ot?.tipo), [config, ot?.tipo]);
  const fase = useMemo(() => faseDe(fases, ot?.faseId), [fases, ot?.faseId]);
  const objetivoTotal = useMemo(() => diasObjetivoTotal(fases), [fases]);
  const coordinador = esCoordinador(config, user?.uid, isOwner);
  const aprobador = esAprobador(config, user?.uid, isOwner);
  const terminada = faseEstaTerminada(ot);
  const enAprobacion = abierta && Boolean(fase?.requiereAprobacion);
  const equiposOt = Array.isArray(ot?.equipos) ? ot.equipos : [];

  const correr = async (fn) => {
    if (busy) return;
    setBusy(true);
    try { await fn(); } catch (e) { console.error('OT action failed', e); } finally { setBusy(false); }
  };

  const toggleEquipo = (id) => correr(() => setEquipos(
    ot, equiposOt.includes(id) ? equiposOt.filter((e) => e !== id) : [...equiposOt, id],
  ));

  const confirmarTermino = () => correr(async () => {
    await terminarFase(ot, notaTermino);
    setNotaTermino('');
    setTerminando(false);
  });

  if (!isOpen || !ot) return null;

  const relacionPartes = [];
  if (ot.clientName) relacionPartes.push({ texto: ot.clientName, to: `/app/clientes/${ot.clienteId}` });
  if (ot.contractTitle || ot.contractNumber) {
    relacionPartes.push({
      texto: `${ot.contractNumber ? `Contrato ${ot.contractNumber}` : 'Contrato'}${ot.contractTitle ? ` · ${ot.contractTitle}` : ''}${ot.contractCreatedAt ? ` (subido ${formatFecha(ot.contractCreatedAt)})` : ''}`,
    });
  }
  if (ot.fianzaNumero) relacionPartes.push({ texto: `Fianza #${ot.fianzaNumero}` });

  const pendientesPreset = {
    clienteId: ot.clienteId || null,
    contratoId: ot.contratoId || null,
    fianzaId: ot.fianzaId || null,
    otId: ot.id,
    equipos: config.equipos,
  };

  return (
    <Dialog open onClose={onClose} size="4xl">
      <DialogBody className="!mt-0">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold tabular-nums text-azul-surelia">{folioTexto(ot.folio)}</span>
              <span className={clsx('rounded-full px-2 py-0.5 text-[11px] font-medium', tipoBadgeClass(ot.tipo))}>
                {ot.tipoNombre || ot.tipo}
              </span>
              {!abierta && (
                <span className="rounded-full px-2 py-0.5 text-[11px] font-medium bg-zinc-200 text-zinc-700">
                  Cerrada · {OT_RESULTADO_LABELS[ot.cierre?.resultado] || ot.cierre?.resultado}
                </span>
              )}
              <span className="text-xs text-zinc-400 tabular-nums">
                {abierta ? `abierta hace ${diasAbierta(ot)} d` : `${diasAbierta(ot)} d de trabajo`}
                {!abierta && objetivoTotal ? ` (objetivo ${objetivoTotal})` : ''}
              </span>
            </div>
            <h2 className="mt-1 text-lg font-semibold text-zinc-900 leading-snug">{ot.titulo}</h2>
            <div className="mt-0.5 text-sm text-zinc-500 flex items-center gap-1 flex-wrap">
              {relacionPartes.length === 0 ? (
                <span className="text-zinc-400">Sin fiado, contrato ni fianza todavía</span>
              ) : relacionPartes.map((p, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <span className="text-zinc-300">·</span>}
                  {p.to ? (
                    <button onClick={() => { onClose(); navigate(p.to); }} className="text-azul-surelia hover:underline inline-flex items-center gap-0.5">
                      {p.texto}<ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
                    </button>
                  ) : <span>{p.texto}</span>}
                </React.Fragment>
              ))}
              {ot.fechaCompromiso && (
                <>
                  <span className="text-zinc-300">·</span>
                  <span className={clsx(abierta && Number(ot.fechaCompromiso) < Date.now() ? 'text-red-600' : '')}>
                    compromiso {formatFecha(ot.fechaCompromiso)}
                  </span>
                </>
              )}
            </div>
            {ot.descripcion && (
              <p className="mt-2 text-sm text-zinc-600 whitespace-pre-wrap">{ot.descripcion}</p>
            )}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button title="Editar" onClick={() => setEditOpen(true)} className="p-1.5 rounded hover:bg-zinc-100 text-zinc-500">
              <PencilSquareIcon className="w-5 h-5" />
            </button>
            {abierta ? (
              <Button outline color="surelia" onClick={() => setCierreModo('cerrar')} disabled={busy}>Cerrar orden</Button>
            ) : (
              <Button outline color="surelia" onClick={() => setCierreModo('reabrir')} disabled={busy}>Reabrir</Button>
            )}
            <button title="Cerrar" onClick={onClose} className="p-1.5 rounded hover:bg-zinc-100 text-zinc-500">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {!abierta && ot.cierre && (
          <div className="mt-3 rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-700">
            Cerrada el {formatFechaHora(ot.cierre.at)}{ot.cierre.nombre ? ` por ${ot.cierre.nombre}` : ''}
            {ot.cierre.motivo ? ` — ${ot.cierre.motivo}` : ''}
          </div>
        )}

        {/* Fase: where the order is, who may declare it done, and the
            coordinador's relevo (Fran, 2026-10-19). */}
        {(() => {
          const idxActual = fases.findIndex((x) => x.id === ot.faseId);
          const siguiente = idxActual >= 0 ? fases[idxActual + 1] : fases[0];
          const estado = estadoFase(ot, fase);
          const dias = diasEnFase(ot);
          return (
            <div className={clsx('mt-4 rounded-lg border px-3 py-2.5', terminada ? 'border-emerald-200 bg-emerald-50/40' : enAprobacion ? 'border-amber-200 bg-amber-50/40' : 'border-zinc-200 bg-zinc-50')}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-xs font-medium text-zinc-600">Fase</div>
                {abierta && (
                  <div className="flex items-center gap-2">
                    {coordinador && !enAprobacion && (
                      <select
                        value=""
                        disabled={busy}
                        onChange={(e) => { if (e.target.value) setPasar({ faseId: e.target.value }); }}
                        className="rounded-lg border border-zinc-300 bg-white px-2 py-1 text-xs text-zinc-600 outline-none focus:border-azul-surelia"
                      >
                        <option value="">Mover a otra fase</option>
                        {fases.filter((f) => f.id !== ot.faseId).map((f) => (
                          <option key={f.id} value={f.id}>{f.nombre}</option>
                        ))}
                      </select>
                    )}
                    {enAprobacion ? (
                      aprobador ? (
                        <>
                          <button
                            disabled={busy}
                            onClick={() => setRechazarOpen(true)}
                            className="inline-flex items-center gap-1 rounded-lg border border-red-300 px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                          >
                            Rechazar
                          </button>
                          <button
                            disabled={busy}
                            onClick={() => setAprobarOpen(true)}
                            className="inline-flex items-center gap-1 rounded-lg bg-azul-surelia px-2.5 py-1 text-xs font-medium text-white hover:bg-azul-surelia/90 disabled:opacity-50"
                          >
                            <ShieldCheckIcon className="w-3.5 h-3.5" />
                            Aprobar
                          </button>
                        </>
                      ) : (
                        <span className="text-xs text-zinc-500">Esperando aprobación</span>
                      )
                    ) : (
                      // Two papeles, two buttons: the operador ends the fase
                      // ("Fase terminada"), the coordinador hands it over
                      // ("Pasar a"). Someone who is both sees both, in that
                      // order. Fran tried collapsing them (2026-10-19) and
                      // asked to bring them back.
                      <>
                        {!terminada && !terminando && (
                          <button
                            disabled={busy}
                            onClick={() => setTerminando(true)}
                            className={clsx(
                              'inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-medium disabled:opacity-50',
                              coordinador ? 'border border-azul-surelia text-azul-surelia hover:bg-azul-surelia/5' : 'bg-azul-surelia text-white hover:bg-azul-surelia/90',
                            )}
                          >
                            <CheckIcon className="w-3.5 h-3.5" />
                            Fase terminada
                          </button>
                        )}
                        {terminada && coordinador && siguiente && (
                          <button
                            disabled={busy}
                            onClick={() => setPasar({ faseId: siguiente.id })}
                            className="inline-flex items-center gap-1 rounded-lg bg-azul-surelia px-2.5 py-1 text-xs font-medium text-white hover:bg-azul-surelia/90 disabled:opacity-50"
                          >
                            Avanzar a {siguiente.nombre}
                            <ChevronRightIcon className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>

              {terminando && (
                <div className="mt-2 flex items-center gap-2">
                  <input
                    type="text"
                    value={notaTermino}
                    maxLength={MOTIVO_MAX_LEN}
                    onChange={(e) => setNotaTermino(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') confirmarTermino(); if (e.key === 'Escape') setTerminando(false); }}
                    placeholder="Nota para quien coordina (opcional)"
                    className={field}
                    autoFocus
                  />
                  <Button color="surelia" onClick={confirmarTermino} disabled={busy}>Confirmar</Button>
                  <Button plain onClick={() => setTerminando(false)} disabled={busy}>Cancelar</Button>
                </div>
              )}

              <ol className="mt-2 flex items-center overflow-x-auto pb-1">
                {fases.map((f, i) => {
                  const actual = f.id === ot.faseId;
                  const pasada = idxActual >= 0 && i < idxActual;
                  return (
                    <li key={f.id} className="flex items-center flex-shrink-0">
                      {i > 0 && <span className={clsx('h-px w-5', pasada || actual ? 'bg-azul-surelia' : 'bg-zinc-300')} />}
                      <span
                        className={clsx(
                          'inline-flex items-center gap-1.5 text-xs',
                          actual ? 'font-semibold text-azul-surelia' : pasada ? 'text-azul-surelia/70' : 'text-zinc-500',
                        )}
                      >
                        <span className={clsx(
                          'inline-flex h-5 w-5 items-center justify-center rounded-full border text-[10px] tabular-nums',
                          actual ? 'border-azul-surelia bg-azul-surelia text-white'
                            : pasada ? 'border-azul-surelia bg-white text-azul-surelia'
                              : 'border-zinc-300 bg-white text-zinc-400',
                        )}>
                          {pasada || (actual && terminada) ? <CheckIcon className="w-3 h-3" /> : f.requiereAprobacion ? <ShieldCheckIcon className="w-3 h-3" /> : i + 1}
                        </span>
                        {f.nombre}
                      </span>
                    </li>
                  );
                })}
                {ot.faseId && !fase && (
                  <li className="text-xs text-amber-600">Fase sin configurar</li>
                )}
              </ol>

              <div className={clsx('mt-1.5 text-xs tabular-nums', terminada ? 'text-emerald-700' : estado === 'vencida' ? 'text-red-600' : estado === 'porVencer' ? 'text-amber-600' : 'text-zinc-500')}>
                {terminada ? (
                  <>
                    {fase?.nombre || 'Fase'} terminada{ot.faseTerminada.nombre ? ` por ${ot.faseTerminada.nombre}` : ''} el {formatFechaHora(ot.faseTerminada.at)}
                    {ot.faseTerminada.nota ? `. ${ot.faseTerminada.nota}` : ''}
                    {' · '}{coordinador ? 'por asignar' : 'esperando a quien coordina'}
                  </>
                ) : fase ? (
                  `${fase.nombre}${enAprobacion ? ' (aprobación)' : ''}: ${dias} ${dias === 1 ? 'día' : 'días'}${fase.diasObjetivo ? ` de ${fase.diasObjetivo} objetivo` : ''}`
                ) : 'Sin fase'}
              </div>
            </div>
          );
        })()}

        {/* Equipos + asignados */}
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <div className="text-xs font-medium text-zinc-600 mb-1">Equipos</div>
            <div className="flex flex-wrap gap-1.5">
              {config.equipos.map((eq) => {
                const on = equiposOt.includes(eq.id);
                return (
                  <button
                    key={eq.id}
                    type="button"
                    disabled={!abierta || busy}
                    onClick={() => toggleEquipo(eq.id)}
                    className={clsx(
                      'rounded-full border px-2.5 py-1 text-xs transition-colors',
                      on ? 'border-azul-surelia bg-azul-surelia text-white' : 'border-gray-300 text-gray-700',
                      abierta ? 'hover:border-azul-surelia/50' : 'opacity-70',
                    )}
                  >
                    {eq.nombre}
                  </button>
                );
              })}
              {config.equipos.length === 0 && <span className="text-xs text-zinc-400">Sin equipos configurados</span>}
            </div>
          </div>
          <div>
            <div className="text-xs font-medium text-zinc-600 mb-1">Asignados</div>
            <AsignadosBubbles
              uids={normalizarAsignados(ot)}
              miembros={miembros}
              meUid={user?.uid}
              onChange={abierta ? (uids) => correr(() => asignar(ot, uids)) : undefined}
              disabled={busy}
            />
          </div>
        </div>

        {/* Body */}
        <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="space-y-4">
            <PendientesPanel
              scope={{ otId: ot.id }}
              preset={pendientesPreset}
              clientName={ot.clientName}
              titulo="Pendientes"
              plano
              vacio="linea"
            />
          </div>

          {/* Capped well under the viewport: the dialog shell adds its own
              vertical padding, so 60vh here made the whole modal scroll. */}
          <section className="rounded-lg border border-zinc-200 bg-white p-3 flex flex-col min-h-[260px] max-h-[44vh]">
            <h3 className="text-sm font-semibold text-zinc-800 mb-2">Hilo</h3>
            <OtHilo
              ot={ot}
              mensajes={mensajes}
              loading={mensajesLoading}
              cerrada={!abierta}
              onEnviar={(texto) => enviarMensaje(ot, texto, mensajes)}
            />
          </section>
        </div>

        {Array.isArray(ot.aprobaciones) && ot.aprobaciones.length > 0 && (
          <div className="mt-3 text-xs text-zinc-500">
            Aprobaciones: {ot.aprobaciones.map((a) => `${a.resultado === 'aprobada' ? 'aprobada' : 'rechazada'} en ${a.faseNombre || a.faseId}${a.nombre ? ` por ${a.nombre}` : ''} (${formatFecha(a.at)})${a.motivo ? `: ${a.motivo}` : ''}`).join(' · ')}
          </div>
        )}
        {isOwner && Array.isArray(ot.cierres) && ot.cierres.length > 0 && (
          <div className="mt-3 text-xs text-zinc-400">
            Cierres anteriores: {ot.cierres.map((c) => `${OT_RESULTADO_LABELS[c.resultado] || c.resultado} (${formatFecha(c.at)})`).join(', ')}
          </div>
        )}
      </DialogBody>

      <OtDialog isOpen={editOpen} onClose={() => setEditOpen(false)} ot={ot} />
      <OtPasarFaseDialog isOpen={Boolean(pasar)} onClose={() => setPasar(null)} ot={ot} faseDestinoId={pasar?.faseId || null} />
      <OtRechazarDialog isOpen={rechazarOpen} onClose={() => setRechazarOpen(false)} ot={ot} />
      <OtAprobarDialog isOpen={aprobarOpen} onClose={() => setAprobarOpen(false)} ot={ot} />
      <OtCerrarDialog
        isOpen={Boolean(cierreModo)}
        modo={cierreModo || 'cerrar'}
        ot={ot}
        onClose={() => setCierreModo(null)}
        onConfirm={cierreModo === 'cerrar' ? (args) => cerrar(ot, args) : (motivo) => reabrir(ot, motivo)}
      />
    </Dialog>
  );
}

export default OtView;
